import type { SingleConnectionGroupItem } from "@wayflyer/flyui";
import { StatusBanner, ButtonGroup, Flex, Text, ConnectionGroup, PageGridColumn, PageGridRow } from "@wayflyer/flyui";
import { useReducer, useState } from "react";
import { useIntl } from "react-intl";
import type { ConnectorPropertiesSchema } from "shared-ui";
import { filterDataSources, getStoreConnectors, KNOWN_UNSUPPORTED_ECOMMERCE, SHOPPING } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { useAirRevenueSourceUpdateMutation } from "../../../../queries/air-response/air-revenue-source.mutations";
import { usePlatformConnectors } from "../../../../queries/connectors/queries";
import type { SelectedAdditionalPlatform } from "../../../../utils/checklist-items";
import {
  getRadioChecklistItemPropsForConnector,
  getRadioChecklistItemPropsForUnsupportedPlatform,
} from "../../../../utils/checklist-items";
import { COMMON_MESSAGES } from "../../../common-messages";
import { MESSAGES } from "../message-definitions";

import type { Step } from "./constants";

interface SelectAdditionalSourceFormProps {
  setQuestionaireStep: (step: Step, connector?: string) => void;
  airPublicIdentifier: string;
  onSubmit: () => void;
}

export const SelectAdditionalSourceForm = ({
  setQuestionaireStep,
  airPublicIdentifier,
  onSubmit,
}: SelectAdditionalSourceFormProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const updateAirRevenueSource = useAirRevenueSourceUpdateMutation(customerDomain);
  const [error, setError] = useState<string | null>(null);
  const { data: connectors } = usePlatformConnectors(customerDomain, { skipConnections: false });
  const filteredSources = filterDataSources(connectors, true);

  const [{ selectedAdditionalPlatform }, dispatchSelectedAdditionalPlatform] = useReducer(
    (
      state: { selectedAdditionalPlatform: SelectedAdditionalPlatform | null },
      action: SelectedAdditionalPlatform
    ): { selectedAdditionalPlatform: SelectedAdditionalPlatform | null } => {
      return {
        selectedAdditionalPlatform: { ...action },
      };
    },
    { selectedAdditionalPlatform: null }
  );

  const isPlatformSelected = (platform: string, isSuppored: boolean) => {
    return !!(
      selectedAdditionalPlatform &&
      selectedAdditionalPlatform.platform === platform &&
      // prevents overlap of supported and unsupported platforms e.g. if freetext matches a supported platform
      selectedAdditionalPlatform.isSupported === isSuppored
    );
  };

  const constructSupportedPlatformChecklistItemProps = (connectors: ConnectorPropertiesSchema[]) => {
    return connectors
      .filter(
        (connector) =>
          connector.multipleConnectionsAllowed ||
          (!connector.multipleConnectionsAllowed && connector.connections.length === 0)
      )
      .map((connector) =>
        getRadioChecklistItemPropsForConnector(
          connector,
          isPlatformSelected(connector.slug, true),
          dispatchSelectedAdditionalPlatform
        )
      );
  };
  const supportedEcommercePlatformOptions: SingleConnectionGroupItem[] = constructSupportedPlatformChecklistItemProps(
    getStoreConnectors(filteredSources)
  );

  const getSelectedUnsupportedPlatformOption = (category: string): SingleConnectionGroupItem | undefined => {
    if (
      selectedAdditionalPlatform &&
      selectedAdditionalPlatform.category === category &&
      !selectedAdditionalPlatform.isSupported
    ) {
      return getRadioChecklistItemPropsForUnsupportedPlatform(
        selectedAdditionalPlatform.platform,
        category,
        true,
        dispatchSelectedAdditionalPlatform
      );
    }
    return undefined;
  };

  const onSelectPlatformSubmit = () => {
    if (!error && selectedAdditionalPlatform) {
      if (selectedAdditionalPlatform.isSupported) {
        setQuestionaireStep("connect_additional_source", selectedAdditionalPlatform.platform);
      } else {
        updateAirRevenueSource
          .mutateAsync({
            airPublicId: airPublicIdentifier,
            noOtherConnectionsReason: "unsupported_store",
            unsupportedSourceName: selectedAdditionalPlatform.platform,
            connectionIds: [],
          })
          .then(() => {
            onSubmit();
          });
      }
    } else {
      setError(intl.formatMessage(MESSAGES.selectRevenuePlatformError));
      // Scroll to top to show the error
      window.scroll({ top: 0, left: 0, behavior: "smooth" });
    }
  };

  return (
    <PageGridColumn $gap="6">
      {error ? (
        <PageGridRow>
          <StatusBanner variant="critical" subheading={error} />
        </PageGridRow>
      ) : null}
      <PageGridRow>
        <Text fontWeight="semiBold" color="heading">
          {intl.formatMessage(MESSAGES.selectRevenuePlatformDescription)}
        </Text>
      </PageGridRow>
      <ConnectionGroup
        selectionMode="single"
        options={supportedEcommercePlatformOptions}
        selectedUnsupportedPlatform={getSelectedUnsupportedPlatformOption(SHOPPING)}
        unsupportedOptions={KNOWN_UNSUPPORTED_ECOMMERCE}
        addUnsupportedPlatform={(platform: string) =>
          dispatchSelectedAdditionalPlatform({ platform: platform, category: SHOPPING, isSupported: false })
        }
      />
      <Flex>
        <ButtonGroup
          primaryAction={{
            onAction: onSelectPlatformSubmit,
            content: selectedAdditionalPlatform?.isSupported
              ? intl.formatMessage(COMMON_MESSAGES.continueLabel)
              : intl.formatMessage(COMMON_MESSAGES.submitLabel),
            analyticsId: `select-revenue-source-${
              selectedAdditionalPlatform?.isSupported ? "continue" : "submit"
            }-button`,
            disabled: !selectedAdditionalPlatform,
          }}
          secondaryAction={{
            onAction: () => setQuestionaireStep("has_additional_platform"),
            content: intl.formatMessage(COMMON_MESSAGES.backLabel),
            analyticsId: "select-revenue-source-back-button",
          }}
        />
      </Flex>
    </PageGridColumn>
  );
};
